import React, { useState, useEffect } from 'react';
import { Calendar, Link, Unlink, RefreshCw, Check } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';

const BookingSetupPage: React.FC = () => {
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, lastSync: null });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    time_slot_duration: 60,
    booking_lead_time_hours: 24,
    allow_same_day_booking: false,
    require_deposit: true,
    booking_buffer_minutes: 30,
    max_advance_booking_days: 90
  });

  useEffect(() => {
    fetchCalendarStatus();
    fetchSettings();
  }, []);

  const fetchCalendarStatus = async () => {
    try {
      const response = await fetch('/api/calendar/status');
      const data = await response.json();
      if (data.success) {
        setCalendarStatus({ connected: data.connected, lastSync: data.lastSync });
      }
    } catch (error) {
      console.error('Error fetching calendar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getBookingSettings', storeId: '372' })
      });
      const data = await response.json();
      if (data.success && data.settings) {
        setSettings({
          time_slot_duration: data.settings.time_slot_duration || 60,
          booking_lead_time_hours: data.settings.booking_lead_time_hours || 24,
          allow_same_day_booking: data.settings.allow_same_day_booking || false,
          require_deposit: data.settings.require_deposit !== false,
          booking_buffer_minutes: data.settings.booking_buffer_minutes || 30,
          max_advance_booking_days: data.settings.max_advance_booking_days || 90
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await fetch('/api/calendar/connect');
      const data = await response.json();
      if (data.success) {
        window.open(data.auth_url, '_blank', 'width=600,height=600');
      }
    } catch (error) {
      console.error('Error connecting calendar:', error);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Google Calendar?')) return;
    try {
      const response = await fetch('/api/calendar/disconnect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setCalendarStatus({ connected: false, lastSync: null });
      }
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch('/api/calendar/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        alert(`Sync complete: ${data.synced} new, ${data.skipped} updated`);
        fetchCalendarStatus();
      } else {
        alert('Sync failed: ' + data.message);
      }
    } catch (error) {
      console.error('Error syncing calendar:', error);
      alert('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'updateBookingSettings',
          storeId: '372',
          settings
        })
      });
      const data = await response.json();
      if (data.success) {
        alert('Settings saved successfully');
      } else {
        alert('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Booking Setup</h1>

        {/* Google Calendar Connection */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <div className="flex items-center mb-4">
            <Calendar className="w-6 h-6 text-primary-600 mr-3" />
            <h2 className="text-xl font-semibold">Google Calendar Integration</h2>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading calendar status...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <p className="font-medium">Connection Status</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {calendarStatus.connected ? 'Connected' : 'Not Connected'}
                  </p>
                  {calendarStatus.lastSync && (
                    <p className="text-xs text-gray-500 mt-1">
                      Last sync: {new Date(calendarStatus.lastSync).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {calendarStatus.connected ? (
                    <>
                      <button
                        onClick={handleSync}
                        disabled={syncing}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
                      >
                        {syncing ? (
                          <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing...</>
                        ) : (
                          <><RefreshCw className="w-4 h-4 mr-2" /> Sync Now</>
                        )}
                      </button>
                      <button
                        onClick={handleDisconnect}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
                      >
                        <Unlink className="w-4 h-4 mr-2" />
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleConnect}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                    >
                      <Link className="w-4 h-4 mr-2" />
                      Connect Google Calendar
                    </button>
                  )}
                </div>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">How it works:</h3>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
                  <li>When connected, your Google Calendar is checked for existing bookings</li>
                  <li>Dates/times booked manually on Google Calendar will show as unavailable</li>
                  <li>Website bookings automatically sync to your Google Calendar</li>
                  <li>Changes to calendar events sync bidirectionally</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Booking Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Booking Settings</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Time Slot Duration (minutes)</label>
              <select
                value={settings.time_slot_duration}
                onChange={(e) => handleSettingChange('time_slot_duration', parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
              >
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="90">1.5 hours</option>
                <option value="120">2 hours</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Minimum Booking Lead Time (hours)</label>
              <input
                type="number"
                value={settings.booking_lead_time_hours}
                onChange={(e) => handleSettingChange('booking_lead_time_hours', parseInt(e.target.value))}
                min="0"
                max="168"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Maximum Advance Booking (days)</label>
              <input
                type="number"
                value={settings.max_advance_booking_days}
                onChange={(e) => handleSettingChange('max_advance_booking_days', parseInt(e.target.value))}
                min="1"
                max="365"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Buffer Between Bookings (minutes)</label>
              <input
                type="number"
                value={settings.booking_buffer_minutes}
                onChange={(e) => handleSettingChange('booking_buffer_minutes', parseInt(e.target.value))}
                min="0"
                max="120"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BookingSetupPage;
